/**
 * Shared building blocks for the registry surfaces (Values, Game Ops,
 * Stewardship). Extracted from the retired GuidanceTab so each tab can
 * compose the views it needs without duplication.
 */
import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";

export const TYPE_COLORS = {
    check:    "bg-sky-900/40 text-sky-200 border-sky-700",
    standard: "bg-violet-900/40 text-violet-200 border-violet-700",
    policy:   "bg-amber-900/40 text-amber-200 border-amber-700",
    value:    "bg-emerald-900/40 text-emerald-200 border-emerald-700",
};

export const CATEGORY_SECTIONS = [
    { root: "values_engine", bucket: "crew_level",    title: "Values Engine — Crew-level" },
    { root: "values_engine", bucket: "game_specific", title: "Values Engine — Game-specific" },
    { root: "release_ops",   bucket: "crew_level",    title: "Release Ops — Crew-level" },
    { root: "release_ops",   bucket: "game_specific", title: "Release Ops — Game-specific" },
];

/** Generic pill-button sub-tab strip used by Values / Obligations /
 *  Stewardship. items: [{k, label, icon?}] */
export function SubTabs({ items, active, onChange, testPrefix = "subtab" }) {
    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`${testPrefix}-strip`}>
            {items.map((t) => {
                const Icon = t.icon;
                return (
                    <button
                        key={t.k}
                        type="button"
                        onClick={() => onChange(t.k)}
                        data-testid={`${testPrefix}-${t.k}-btn`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide border rounded transition-colors ${
                            active === t.k
                                ? "bg-volt/20 text-volt border-volt/60"
                                : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200"
                        }`}
                    >
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
}

export function TypePill({ type }) {
    const cls = TYPE_COLORS[type] || "bg-zinc-800 text-zinc-300 border-zinc-700";
    return (
        <span
            className={`pill text-[9px] font-bold uppercase border ${cls}`}
            data-testid={`registry-type-pill-${type}`}
        >
            {type}
        </span>
    );
}

export function RegistryRow({ row, showValueGroup = false }) {
    const isRetired = row.status === "retired";
    return (
        <div
            className={`border border-zinc-800 rounded-md p-3 text-xs bg-black/40 ${isRetired ? "opacity-50" : ""}`}
            data-testid={`registry-row-${row.name}`}
            data-status={row.status}
        >
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <code className="text-sm font-bold text-white">{row.name}</code>
                <TypePill type={row.type} />
                {row.severity && (
                    <span
                        className={`pill text-[9px] font-bold uppercase border ${
                            row.severity === "critical" ? "bg-rose-600/20 text-rose-300 border-rose-500/60"
                            : row.severity === "high" ? "bg-rose-500/10 text-rose-400 border-rose-500/40"
                            : row.severity === "med" ? "bg-amber-500/10 text-amber-400 border-amber-500/40"
                            : "bg-zinc-700/30 text-zinc-400 border-zinc-700"
                        }`}
                        title="Severity — governed in Stewardship → Severity (read-only here)"
                        data-testid={`registry-severity-${row.name}`}
                    >
                        {row.severity}
                    </span>
                )}
                {row.domain && (
                    <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-300 border-zinc-700">
                        {row.domain}
                    </span>
                )}
                {isRetired && (
                    <span
                        className="pill text-[9px] font-bold uppercase border bg-rose-900/40 text-rose-200 border-rose-700"
                        data-testid={`registry-status-retired-${row.name}`}
                    >
                        Retired
                    </span>
                )}
                {row.future_canon_id && (
                    <span
                        className="pill text-[9px] font-bold uppercase border bg-zinc-900 text-emerald-300 border-emerald-800"
                        title="Canon-ready — will point at Canon when it lands"
                        data-testid={`registry-canon-${row.name}`}
                    >
                        Canon: {row.future_canon_id}
                    </span>
                )}
                {showValueGroup && row.value_group && (
                    <span
                        className="pill text-[9px] font-bold uppercase border bg-zinc-900 text-fuchsia-300 border-fuchsia-800"
                        data-testid={`registry-vgroup-${row.name}`}
                    >
                        {row.value_group}
                    </span>
                )}
            </div>
            <div className="text-zinc-300 mb-2">{row.description}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[10px] text-zinc-500">
                <div><span className="text-zinc-600">Impl:</span> <code className="text-zinc-400">{row.implementation}</code></div>
                <div><span className="text-zinc-600">Source:</span> <span className="text-zinc-400">{row.data_source}</span></div>
            </div>
        </div>
    );
}

export function CountsStrip({ counts }) {
    if (!counts) return null;
    const byType = counts.by_type || {};
    return (
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-zinc-400 mb-4" data-testid="registry-counts-strip">
            <span data-testid="registry-counts-total">
                <span className="text-zinc-500">Total:</span>{" "}
                <span className="text-white font-bold">{counts.total}</span>
            </span>
            <span data-testid="registry-counts-crew">
                <span className="text-zinc-500">Crew-level:</span>{" "}
                <span className="text-white font-bold">{counts.crew_level}</span>
            </span>
            <span data-testid="registry-counts-game">
                <span className="text-zinc-500">Game-specific:</span>{" "}
                <span className="text-white font-bold">{counts.game_specific}</span>
            </span>
            <span className="text-zinc-700">|</span>
            {["check", "standard", "policy", "value"].map((t) => (
                <span key={t} className="flex items-center gap-1" data-testid={`registry-counts-type-${t}`}>
                    <TypePill type={t} />
                    <span className="text-white font-bold">{byType[t] ?? 0}</span>
                </span>
            ))}
        </div>
    );
}

/**
 * By-Value view — six value_groups as parent cards, preceded by a
 * read-only Pending Classification section (interactive review lives in
 * Stewardship → Actions and the Release Ops Values Engine card).
 */
export function ByValueView({ data }) {
    const rowByName = useMemo(() => {
        const map = {};
        for (const root of ["values_engine", "release_ops"]) {
            for (const bucket of ["crew_level", "game_specific"]) {
                for (const row of data[root]?.[bucket] ?? []) {
                    map[row.name] = row;
                }
            }
        }
        return map;
    }, [data]);

    const slugs = Object.keys(data.value_groups || {});

    return (
        <div className="space-y-6" data-testid="guidance-by-value-view">
            {(data.pending_entries?.length ?? 0) > 0 && (
                <section
                    className="border-2 border-amber-900/50 rounded-lg p-4 bg-amber-950/10"
                    data-testid="value-group-pending"
                >
                    <header className="mb-3">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <AlertTriangle className="w-4 h-4 text-amber-400" />
                            <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-amber-200">
                                Pending Classification
                            </h3>
                            <span className="pill text-[9px] font-bold uppercase border bg-amber-900/40 text-amber-200 border-amber-700">
                                {data.pending_entries.length} awaiting steward
                            </span>
                        </div>
                        <div className="text-xs text-zinc-400 italic">
                            Awaiting your review. Accept or override in Stewardship → Actions, or in Release Ops → Values Engine.
                        </div>
                    </header>
                    <div className="space-y-2">
                        {data.pending_entries.map((pe) => (
                            <div
                                key={pe.name}
                                className="border border-zinc-800 rounded-md p-3 text-xs bg-black/40"
                                data-testid={`pending-readonly-${pe.name}`}
                            >
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <code className="text-sm font-bold text-white">{pe.name}</code>
                                    <TypePill type={pe.type} />
                                    <span className="pill text-[9px] font-bold uppercase border bg-zinc-900 text-fuchsia-300 border-fuchsia-800">
                                        Proposed: {pe.proposed_value_group || "—"}
                                    </span>
                                </div>
                                {pe.proposal_notes && (
                                    <div className="text-[10px] text-zinc-400">{pe.proposal_notes}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {slugs.map((slug) => {
                const node = data.value_groups[slug];
                const totalKids = node.children.length;
                return (
                    <section
                        key={slug}
                        className="border-2 border-fuchsia-900/40 rounded-lg p-4 bg-fuchsia-950/10"
                        data-testid={`value-group-${slug.replace(/[^a-z0-9-]/gi, "_")}`}
                    >
                        <header className="mb-3">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-fuchsia-200">
                                    {slug}
                                </h3>
                                <span className="pill text-[9px] font-bold uppercase border bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700">
                                    Value
                                </span>
                                <span className="text-[10px] text-zinc-500">
                                    {node.active_children}/{totalKids} active
                                </span>
                            </div>
                            <div className="text-xs text-zinc-300 italic">
                                “{node.statement}”
                            </div>
                        </header>
                        <div className="space-y-2">
                            {node.children.length === 0 ? (
                                <div className="text-[11px] text-zinc-600 italic">
                                    No committed children yet — entries may still be pending classification.
                                </div>
                            ) : (
                                node.children.map((c) => {
                                    const full = rowByName[c.name];
                                    if (!full) return null;
                                    return <RegistryRow key={c.name} row={full} />;
                                })
                            )}
                        </div>
                    </section>
                );
            })}

            <section
                className="border-2 border-zinc-800 rounded-lg p-4"
                data-testid="value-group-unassigned-game-specific"
            >
                <header className="mb-3">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-300">
                            Unassigned · Game-specific
                        </h3>
                        <span className="pill text-[9px] font-bold uppercase border bg-zinc-800 text-zinc-400 border-zinc-700">
                            No Value Group
                        </span>
                    </div>
                    <div className="text-xs text-zinc-500 italic">
                        Game-specific entries don't ladder up to crew values. They live outside the taxonomy by design — see the Game Ops tab.
                    </div>
                </header>
                <div className="space-y-2">
                    {["values_engine", "release_ops"].flatMap((root) =>
                        (data[root]?.game_specific ?? []).map((row) => (
                            <RegistryRow key={row.name} row={row} />
                        ))
                    )}
                </div>
            </section>
        </div>
    );
}

export function ByCategoryView({ data }) {
    return (
        <div className="space-y-6" data-testid="guidance-by-category-view">
            {CATEGORY_SECTIONS.map((s) => {
                const rows = data[s.root]?.[s.bucket] ?? [];
                return (
                    <section
                        key={`${s.root}.${s.bucket}`}
                        className="border border-zinc-800 rounded-lg p-4"
                        data-testid={`registry-section-${s.root}-${s.bucket}`}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                                {s.title}
                            </h3>
                            <span className="text-[10px] text-zinc-500">({rows.length})</span>
                        </div>
                        {rows.length === 0 ? (
                            <div className="text-[11px] text-zinc-600 italic">No entries.</div>
                        ) : (
                            <div className="space-y-2">
                                {rows.map((row) => (
                                    <RegistryRow key={row.name} row={row} showValueGroup />
                                ))}
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
}
